// LocalStorage-backed Munos KV store. Temporary phase-0 backing for
// the client side, pending the move to SQLite-WASM — see
// docs/persistence.md §Backing stores.
//
// Storage layout: every entry sits under the key prefix
//   munos:<scope>:<namespace>:<key>
// with the value stored as a base64-encoded string. The runtime supplies
// <scope> per call (the fetch-URL identity of the calling script, or the
// shared "@" sentinel) — see docs/persistence.md §"Namespaces and scope"
// — so one script's private data is walled off from another's, and the
// `@` tier is the one shared space. The components never need splitting:
// every key is built and matched through fullKey, so a scope containing
// ':' (a URL does) is fine.
//
// Quota: per-(scope, namespace) byte budget, defaulting to 1 MiB to
// match the spec's suggested client default. Tracked in raw bytes (the
// contract the script sees), not in base64-inflated string length. A
// write that would push the bucket past its quota fails with
// `infraError: "quota_exceeded"`, leaving the existing value intact.
// We rebuild the usage tally lazily on first access per (scope,
// namespace) by walking matching LocalStorage keys, which keeps state
// consistent across page reloads without forcing an upfront scan of the
// entire store.

import { type KvWriteResult, type MunosKvBackend, DEFAULT_KV_MAX_KEYS, pageKeys } from "@multinostalgia/munos";

// The backend contract lives in @multinostalgia/munos (runtime/kv.ts)
// — shared with the server's SQLite backend and the reference host.
// Re-exported so existing web-side importers keep working.
export type { KvWriteResult, MunosKvBackend } from "@multinostalgia/munos";

const DEFAULT_QUOTA_BYTES = 1024 * 1024;

export interface LocalStorageMunosKvOptions {
  // Per-namespace quota in raw bytes. Spec default for the client side
  // is 1 MiB.
  quotaPerNamespace?: number;
  // Per-(scope, namespace) key-count ceiling. Defaults to DEFAULT_KV_MAX_KEYS.
  maxKeysPerNamespace?: number;
  // Inject a Storage implementation for tests. Defaults to
  // globalThis.localStorage; tests in Node pass an in-memory shim.
  storage?: Storage;
}

export class LocalStorageMunosKv implements MunosKvBackend {
  private readonly quotaPerNamespace: number;
  private readonly maxKeysPerNamespace: number;
  private readonly storage: Storage;
  // Cached per-(scope, namespace) usage in raw bytes (not base64 length) and
  // key count. Lazily populated by primeUsage; keyed by the bucket() composite.
  private readonly usage = new Map<string, number>();
  private readonly keyCount = new Map<string, number>();
  private readonly primed = new Set<string>();

  constructor(opts: LocalStorageMunosKvOptions = {}) {
    this.quotaPerNamespace = opts.quotaPerNamespace ?? DEFAULT_QUOTA_BYTES;
    this.maxKeysPerNamespace = opts.maxKeysPerNamespace ?? DEFAULT_KV_MAX_KEYS;
    this.storage = opts.storage ?? globalThis.localStorage;
  }

  // LocalStorage is synchronous; each method resolves immediately to
  // satisfy the async MunosKvBackend port.
  get(scope: string, ns: string, key: string): Promise<Uint8Array | undefined> {
    const raw = this.storage.getItem(this.fullKey(scope, ns, key));
    return Promise.resolve(raw === null ? undefined : base64ToBytes(raw));
  }

  set(scope: string, ns: string, key: string, data: Uint8Array): Promise<KvWriteResult> {
    const bucket = this.primeUsage(scope, ns);
    const fullKey = this.fullKey(scope, ns, key);
    const existing = this.storage.getItem(fullKey);
    const existingLen = existing === null ? 0 : base64ToBytes(existing).length;
    const projected = (this.usage.get(bucket) ?? 0) - existingLen + data.length;
    if (projected > this.quotaPerNamespace) {
      return Promise.resolve({ ok: false, infraError: "quota_exceeded" });
    }
    // Key-count ceiling: a brand-new key past the limit is refused; an
    // overwrite (existing !== null) adds no key, so it stays allowed.
    const isNew = existing === null;
    if (isNew && (this.keyCount.get(bucket) ?? 0) >= this.maxKeysPerNamespace) {
      return Promise.resolve({ ok: false, infraError: "quota_exceeded" });
    }
    try {
      this.storage.setItem(fullKey, bytesToBase64(data));
    } catch (e) {
      // LocalStorage's own global quota (typically 5–10 MB per origin
      // across all keys) may bite even when our per-bucket tally says we
      // have room. Surface it as a separate infra failure so the
      // script's db_error handler can distinguish.
      return Promise.resolve({ ok: false, infraError: `localstorage_full: ${(e as Error).message}` });
    }
    this.usage.set(bucket, projected);
    if (isNew) this.keyCount.set(bucket, (this.keyCount.get(bucket) ?? 0) + 1);
    return Promise.resolve({ ok: true });
  }

  del(scope: string, ns: string, key: string): Promise<void> {
    const bucket = this.primeUsage(scope, ns);
    const fullKey = this.fullKey(scope, ns, key);
    const existing = this.storage.getItem(fullKey);
    if (existing === null) return Promise.resolve();
    const existingLen = base64ToBytes(existing).length;
    this.storage.removeItem(fullKey);
    this.usage.set(bucket, Math.max(0, (this.usage.get(bucket) ?? 0) - existingLen));
    this.keyCount.set(bucket, Math.max(0, (this.keyCount.get(bucket) ?? 0) - 1));
    return Promise.resolve();
  }

  list(scope: string, ns: string, prefix: string, start = "", limit = 0, reverse = false): Promise<string[]> {
    const fullPrefix = this.fullKey(scope, ns, prefix);
    const keyPrefixForNs = this.fullKey(scope, ns, "");
    const matched: string[] = [];
    for (let i = 0; i < this.storage.length; i++) {
      const raw = this.storage.key(i);
      if (raw === null) continue;
      if (!raw.startsWith(fullPrefix)) continue;
      // Strip the `munos:<scope>:<ns>:` shell so the script sees only
      // the script-meaningful key.
      matched.push(raw.slice(keyPrefixForNs.length));
    }
    // LocalStorage has no ordered index — enumeration order is unspecified — so
    // the ordered page is produced in JS via the shared pageKeys (same
    // definition every tier funnels through in makeDbEnv).
    return Promise.resolve(pageKeys(matched, prefix, start, limit, reverse));
  }

  // ----- internals -----

  private fullKey(scope: string, ns: string, key: string): string {
    return `munos:${scope}:${ns}:${key}`;
  }

  // The usage-cache key for a (scope, namespace) bucket. NUL-separated so
  // it can't be confused with any real scope/ns pair.
  private bucket(scope: string, ns: string): string {
    return `${scope} ${ns}`;
  }

  // Prime the lazy usage tally for a bucket on first touch; returns the
  // bucket key so callers reuse it without recomputing.
  private primeUsage(scope: string, ns: string): string {
    const bucket = this.bucket(scope, ns);
    if (this.primed.has(bucket)) return bucket;
    const keyPrefixForNs = this.fullKey(scope, ns, "");
    let total = 0;
    let count = 0;
    for (let i = 0; i < this.storage.length; i++) {
      const raw = this.storage.key(i);
      if (raw === null) continue;
      if (!raw.startsWith(keyPrefixForNs)) continue;
      const val = this.storage.getItem(raw);
      if (val === null) continue;
      total += base64ToBytes(val).length;
      count++;
    }
    this.usage.set(bucket, total);
    this.keyCount.set(bucket, count);
    this.primed.add(bucket);
    return bucket;
  }
}

// ----- base64 helpers -----
//
// We avoid string-concat in a tight loop (atob/btoa are themselves
// O(n) but adding +=" " inside the loop pushes browsers into quadratic
// behavior for large blobs). The chunked builder keeps both directions
// linear up to typical save-file sizes.

function bytesToBase64(bytes: Uint8Array): string {
  let s = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    s += String.fromCharCode(...bytes.subarray(i, Math.min(i + CHUNK, bytes.length)));
  }
  return btoa(s);
}

function base64ToBytes(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i) & 0xff;
  return out;
}
